import { useEffect, useMemo, useState } from 'react';
import { Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';

import type { Order, OrderEmailPayload } from '../../types/orders';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const MAX_EXTRA_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.txt',
]);
const ALLOWED_ATTACHMENT_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
]);

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const getDefaultSubject = (order: Order) => `Pedido InteriorLab ${order.order_number || 'sin-numero'}`;

const getDefaultBody = (order: Order) => {
  const shippingLocation = order.shipping_location ?? 'pendiente de confirmar';
  return [
    'Hola,',
    '',
    `Te enviamos adjunto el pedido InteriorLab ${order.order_number || ''} en formato PDF.`,
    `Lugar de envío: ${shippingLocation}.`,
    'Quedamos atentos para cualquier duda.',
    '',
    'Gracias.',
  ].join('\n');
};

export interface OrderEmailDialogProps {
  open: boolean;
  order: Order | null;
  sending: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (payload: OrderEmailPayload) => Promise<void>;
}

export function OrderEmailDialog({ open, order, sending, onOpenChange, onSend }: OrderEmailDialogProps) {
  const [customMode, setCustomMode] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [extraAttachments, setExtraAttachments] = useState<File[]>([]);

  useEffect(() => {
    if (!open || !order) return;
    setCustomMode(false);
    setToEmail(order.supplier_email ?? '');
    setSubject(getDefaultSubject(order));
    setBody(getDefaultBody(order));
    setExtraAttachments([]);
  }, [open, order]);

  const pdfFileName = useMemo(
    () => (order ? `pedido-${order.order_number || order.id}.pdf` : 'pedido.pdf'),
    [order],
  );

  const handleSubmit = async () => {
    if (!order) return;
    const normalizedToEmail = toEmail.trim();
    const normalizedSubject = subject.trim();
    const normalizedBody = body.trim();

    if (!isValidEmail(normalizedToEmail)) {
      toast.error('Introduce un email de destinatario válido.');
      return;
    }
    if (!normalizedSubject) {
      toast.error('El asunto es obligatorio.');
      return;
    }
    if (!normalizedBody) {
      toast.error('El cuerpo del mensaje es obligatorio.');
      return;
    }
    if (extraAttachments.length > MAX_EXTRA_ATTACHMENTS) {
      toast.error(`Puedes adjuntar como máximo ${MAX_EXTRA_ATTACHMENTS} archivos adicionales.`);
      return;
    }
    for (const file of extraAttachments) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`El archivo ${file.name} supera el límite de 10MB.`);
        return;
      }
      const extension = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.')).toLowerCase() : '';
      const hasAllowedExtension = ALLOWED_ATTACHMENT_EXTENSIONS.has(extension);
      const hasAllowedMime = ALLOWED_ATTACHMENT_MIME_TYPES.has(file.type);
      if (!hasAllowedExtension && !hasAllowedMime) {
        toast.error(`El tipo de archivo no está permitido: ${file.name}`);
        return;
      }
    }

    await onSend({
      to_email: normalizedToEmail,
      subject: normalizedSubject,
      body: normalizedBody,
      extra_attachments: extraAttachments,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !sending && onOpenChange(nextOpen)}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Enviar pedido por email</DialogTitle>
          <DialogDescription>
            Se adjuntará automáticamente el PDF del pedido. Pulsa &quot;Personalizar&quot; para editar campos y añadir más adjuntos.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="order-email-to">Destinatario</Label>
            <Input
              id="order-email-to"
              value={toEmail}
              onChange={(event) => setToEmail(event.target.value)}
              disabled={!customMode || sending}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="order-email-subject">Asunto</Label>
            <Input
              id="order-email-subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              disabled={!customMode || sending}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="order-email-body">Mensaje</Label>
            <Textarea
              id="order-email-body"
              rows={8}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              disabled={!customMode || sending}
              className="min-h-[180px] max-h-[320px] resize-y"
            />
          </div>

          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900">Adjunto fijo</div>
            <div className="mt-1 inline-flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              <span>{pdfFileName}</span>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="order-email-extra-attachments">Adjuntos adicionales (opcional)</Label>
            <Input
              id="order-email-extra-attachments"
              type="file"
              multiple
              disabled={!customMode || sending}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt"
              onChange={(event) => setExtraAttachments(Array.from(event.target.files ?? []))}
            />
            {extraAttachments.length > 0 ? (
              <div className="space-y-1 text-xs text-gray-600">
                {extraAttachments.map((file) => (
                  <p key={`${file.name}-${file.size}`}>{file.name}</p>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancelar
          </Button>
          {!customMode ? (
            <Button variant="outline" onClick={() => setCustomMode(true)} disabled={sending}>
              Personalizar
            </Button>
          ) : null}
          <Button onClick={handleSubmit} disabled={sending}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Enviar mensaje
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
